import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Terms = () => {
  const { t, i18n } = useTranslation();

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('terms.title')} | Tap'nGrab</title>
        <meta name="description" content="Terms of service for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t('terms.title')} | Tap'nGrab`} />
        <meta property="og:description" content="Terms of service for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta property="og:url" content={`https://tapngrab.ca/${i18n.language === 'fr' ? 'fr/' : ''}terms`} />
        <meta property="og:image" content="https://tapngrab.ca/images/smart-store.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content={i18n.language === 'fr' ? 'fr_CA' : 'en_CA'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t('terms.title')} | Tap'nGrab`} />
        <meta name="twitter:description" content="Terms of service for Tap'nGrab smart coolers service in Montreal and South Shore, Quebec." />
        <meta name="twitter:image" content="https://tapngrab.ca/images/smart-store.png" />
        <link rel="canonical" href="https://tapngrab.ca/terms" />
        <link rel="alternate" hreflang="fr" href="https://tapngrab.ca/fr/terms" />
        <link rel="alternate" hreflang="en" href="https://tapngrab.ca/terms" />
      </Helmet>

      <div className="text-slate-800 bg-white">
        <Header />
        
        {/* Hero Section */}
        <section className="hero-grad py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-slate-900 sm:text-5xl lg:text-6xl">
                {t('terms.title')}
              </h1>
              <p className="mt-6 text-lg text-slate-600">
                {t('terms.last_updated')}
              </p>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="bg-white py-16 sm:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.acceptance')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.acceptance_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.description_service')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.service_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('terms.service_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.service_eligibility')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.eligibility_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('terms.eligibility_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.user_obligations')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.obligations_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('terms.obligations_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.intellectual_property')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.ip_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.limitation_liability')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.liability_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.service_availability')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.availability_description')}
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-8 space-y-6">
                <h2 className="text-2xl font-bold text-slate-900">
                  {t('terms.payment_billing')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.payment_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.privacy_data')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.privacy_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.termination')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.termination_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.governing_law')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.law_description')}
                </p>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.changes_terms')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.changes_description')}
                </p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-8 space-y-6">
                <h2 className="text-2xl font-bold text-slate-900">
                  {t('terms.contact_info')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.contact_description')}
                </p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  {t('terms.contact_list', { returnObjects: true }).map((item, index) => (
                    <li key={index} className="leading-relaxed">{item}</li>
                  ))}
                </ul>
              </div>

              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-slate-900 border-b border-slate-200 pb-2">
                  {t('terms.severability')}
                </h2>
                <p className="text-slate-700 leading-relaxed">
                  {t('terms.severability_description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Terms;
